import type { ReactNode } from "react";
import { useI18n } from "../i18n";
import { useTheme } from "../lib/theme";

/** Where the 3D viewport sits in a 1600×960 capture (CSS px), used to overlay the orbit video exactly on it. */
const VIEWPORT = { left: 268 / 1600, top: 120 / 960, width: 952 / 1600, height: 764 / 960 };

/**
 * Real FormaCAD capture for marketing pages. Picks the file for the current
 * language and site theme (`/docs/screens/<scene>[.en].<theme>.webp`), so
 * switching either swaps the image instantly. `video` names a recording of the
 * 3D viewport only (`<video>[.en].<theme>.webm`, 60 fps loop) laid exactly over
 * the viewport of the still, so the interface stays a sharp image and only the
 * model moves; reduced motion keeps the still. Optional overlay children are
 * positioned over the frame (callout chips).
 */
export function ProductShot({ scene, video, alt, width = 1600, height = 960, priority = false, className = "", children }: { scene: string; video?: string; alt: string; width?: number; height?: number; priority?: boolean; className?: string; children?: ReactNode }) {
  const { locale } = useI18n();
  const theme = useTheme();
  const variant = `${locale === "en" ? ".en" : ""}.${theme}`;
  const src = `/docs/screens/${scene}${variant}.webp`;
  const motion = video && !(typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const pct = (v: number) => `${(v * 100).toFixed(4)}%`;
  return (
    <figure className={`product-shot ${className}`}>
      <div className="product-shot-window">
        <div className="product-shot-bar" aria-hidden>
          <span /><span /><span />
        </div>
        <div className="product-shot-media">
          <img src={src} alt={alt} width={width} height={height} loading={priority ? "eager" : "lazy"} decoding="async" {...(priority ? { fetchPriority: "high" as const } : {})} />
          {motion && (
            <video
              key={variant}
              className="product-shot-motion"
              src={`/docs/screens/${video}${variant}.webm`}
              style={{ left: pct(VIEWPORT.left), top: pct(VIEWPORT.top), width: pct(VIEWPORT.width), height: pct(VIEWPORT.height) }}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden
            />
          )}
        </div>
      </div>
      {children}
    </figure>
  );
}
